import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LanguageContext } from '../App';
import { useAuth } from '../context/AuthContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { EyeIcon, EyeOffIcon, LogInIcon, AlertCircleIcon, CheckIcon } from 'lucide-react';
import { pageTransition, fadeIn, slideUp } from '../utils/animations';
export const SignIn: React.FC = () => {
  const {
    language,
    direction
  } = useContext(LanguageContext);
  const {
    login,
    error,
    isAuthenticated
  } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDefaultCredentials, setShowDefaultCredentials] = useState(false);
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const success = await login(email, password);
      if (success) {
        navigate('/dashboard');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const toggleShowDefaultCredentials = () => {
    setShowDefaultCredentials(!showDefaultCredentials);
    if (!showDefaultCredentials) {
      setEmail('admin@riders.com');
      setPassword('password123');
    }
  };
  return <motion.div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8" variants={pageTransition} initial="hidden" animate="visible" exit="exit" dir={direction} data-id="element-1320">
      <div className="absolute top-4 right-4" data-id="element-1321">
        <LanguageSwitcher data-id="element-1322" />
      </div>
      <motion.div className="sm:mx-auto sm:w-full sm:max-w-md" variants={slideUp} data-id="element-1323">
        <div className="text-center" data-id="element-1324">
          <motion.div className="mx-auto h-16 w-16 rounded-full bg-congress-blue-100 flex items-center justify-center" whileHover={{
          scale: 1.05
        }} data-id="element-1325">
            <LogInIcon size={32} className="text-congress-blue-600" data-id="element-1326" />
          </motion.div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900" data-id="element-1327">
            {language === 'en' ? 'Sign in to RIDERS' : 'تسجيل الدخول إلى RIDERS'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600" data-id="element-1328">
            {language === 'en' ? 'Access the Kuwait delivery admin dashboard' : 'الوصول إلى لوحة تحكم توصيل الكويت'}
          </p>
        </div>
      </motion.div>
      <motion.div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md" variants={fadeIn} data-id="element-1329">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10" data-id="element-1330">
          <form className="space-y-6" onSubmit={handleSubmit} data-id="element-1331">
            {error && <motion.div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-start" initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} data-id="element-1332">
                <AlertCircleIcon size={20} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-1333" />
                <span data-id="element-1334">{error}</span>
              </motion.div>}
            {/* Email field */}
            <div data-id="element-1335">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-1336">
                {language === 'en' ? 'Email address' : 'البريد الإلكتروني'}
              </label>
              <div className="mt-1" data-id="element-1337">
                <input id="email" name="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-congress-blue-500 focus:border-congress-blue-500 sm:text-sm" placeholder={language === 'en' ? 'Enter your email' : 'أدخل بريدك الإلكتروني'} data-id="element-1338" />
              </div>
            </div>
            {/* Password field */}
            <div data-id="element-1339">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700" data-id="element-1340">
                {language === 'en' ? 'Password' : 'كلمة المرور'}
              </label>
              <div className="mt-1 relative" data-id="element-1341">
                <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-congress-blue-500 focus:border-congress-blue-500 sm:text-sm" placeholder={language === 'en' ? 'Enter your password' : 'أدخل كلمة المرور'} data-id="element-1342" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" onClick={() => setShowPassword(!showPassword)} data-id="element-1343">
                  {showPassword ? <EyeOffIcon size={18} aria-hidden="true" data-id="element-1344" /> : <EyeIcon size={18} aria-hidden="true" data-id="element-1345" />}
                </button>
              </div>
            </div>
            {/* Remember me & Forgot password */}
            <div className="flex items-center justify-between" data-id="element-1346">
              <div className="flex items-center" data-id="element-1347">
                <input id="remember-me" name="remember-me" type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="h-4 w-4 text-congress-blue-600 focus:ring-congress-blue-500 border-gray-300 rounded" data-id="element-1348" />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900" data-id="element-1349">
                  {language === 'en' ? 'Remember me' : 'تذكرني'}
                </label>
              </div>
              <div className="text-sm" data-id="element-1350">
                <a href="#" className="font-medium text-congress-blue-600 hover:text-congress-blue-500" data-id="element-1351">
                  {language === 'en' ? 'Forgot your password?' : 'نسيت كلمة المرور؟'}
                </a>
              </div>
            </div>
            {/* Sign in button */}
            <div data-id="element-1352">
              <button type="submit" disabled={isSubmitting} className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-congress-blue-600 hover:bg-congress-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-congress-blue-500 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-1353">
                {isSubmitting ? <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1354">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1355"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1356"></path>
                  </svg> : <LogInIcon size={18} className={language === 'en' ? 'mr-2' : 'ml-2 order-2'} data-id="element-1357" />}
                {language === 'en' ? 'Sign in' : 'تسجيل الدخول'}
              </button>
            </div>
          </form>
          {/* Default credentials helper */}
          <div className="mt-6" data-id="element-1358">
            <button type="button" onClick={toggleShowDefaultCredentials} className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-congress-blue-500" data-id="element-1359">
              {showDefaultCredentials ? <CheckIcon size={18} className={language === 'en' ? 'mr-2' : 'ml-2 order-2'} data-id="element-1360" /> : null}
              {language === 'en' ? 'Use default credentials' : 'استخدام بيانات الاعتماد الافتراضية'}
            </button>
            {showDefaultCredentials && <motion.div className="mt-4 bg-gray-50 border border-gray-200 rounded-md p-4 text-sm" initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} data-id="element-1361">
                <div className="font-medium text-gray-700 mb-1" data-id="element-1362">
                  {language === 'en' ? 'Default credentials:' : 'بيانات الاعتماد الافتراضية:'}
                </div>
                <div className="grid grid-cols-2 gap-2" data-id="element-1363">
                  <div className="text-gray-500" data-id="element-1364">
                    {language === 'en' ? 'Email:' : 'البريد الإلكتروني:'}
                  </div>
                  <div className="text-gray-900 font-mono" data-id="element-1365">
                    admin@riders.com
                  </div>
                  <div className="text-gray-500" data-id="element-1366">
                    {language === 'en' ? 'Password:' : 'كلمة المرور:'}
                  </div>
                  <div className="text-gray-900 font-mono" data-id="element-1367">password123</div>
                </div>
              </motion.div>}
          </div>
        </div>
      </motion.div>
    </motion.div>;
};